import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { getTopicsForGrade } from './utils/topics';
import './TopicSelector.css';

const DESKTOP_PATH =
  'M 175 75 Q 250 90, 425 175 T 675 275 T 925 375 Q 950 425, 825 525 T 575 675 T 325 825';
const MOBILE_PATH =
  'M 200 50 Q 150 150, 200 250 Q 250 350, 200 450 Q 150 550, 200 650 Q 250 750, 200 850 Q 150 950, 200 1050 Q 250 1150, 200 1250 Q 150 1350, 200 1450 Q 250 1550, 200 1650';

function TopicSelector({ onTopicSelect, onBackToGradeSelection, selectedGrade }) {
  const [availableTopics, setAvailableTopics] = useState([]);
  const [loading, setLoading] = useState(true);

  const gradeDisplayNames = {
    grade7: "שכבות ז'-ט'",
    grade10: 'שכבה י׳',
    grade11: 'שכבה י״א',
    grade12: 'שכבה י״ב'
  };

  const potentialTopics = useMemo(
    () => getTopicsForGrade(selectedGrade).filter(topic => !topic.hidden),
    [selectedGrade]
  );

  useEffect(() => {
    const checkAvailableTopics = async () => {
      setLoading(true);
      const availableFiles = [];
      const basePath = process.env.PUBLIC_URL || '';
      const buildUrl = (file) => `${basePath}/${file}`.replace(/([^:]\/)\/+/g, '$1');

      for (const topic of potentialTopics) {
        try {
          const response = await fetch(buildUrl(topic.file));
          if (response.ok) {
            const csvText = await response.text();
            const lines = csvText.trim().split('\n');
            if (lines.length > 0) {
              const firstLine = lines[0].split(',');
              if (firstLine.length >= 2 && firstLine[0].trim() && firstLine[1].trim()) {
                availableFiles.push(topic);
              }
            }
          } else {
            // If the file is missing, still surface the topic so users can report issues
            availableFiles.push(topic);
          }
        } catch (error) {
          // Even if fetch fails (e.g., hosted under a subpath), keep the topic visible
          availableFiles.push(topic);
        }
      }

      setAvailableTopics(availableFiles);
      setLoading(false);
    };

    checkAvailableTopics();
  }, [selectedGrade, potentialTopics]);

  const handleTopicSelect = (topic) => {
    onTopicSelect(topic);
  };

  const navigate = useNavigate();
  const { grade, subject } = useParams();

  const handleBackToSubjects = () => {
    if (grade) {
      navigate(`/grade/${grade}`);
    } else {
      navigate('/junior-high');
    }
  };

  const subjectNames = {
    vocabulary: 'אוצר מילים',
    verbs: 'פועל',
    syntax: 'תחביר',
    spoken: 'דיבור'
  };

  if (loading) {
    return (
      <div className="topic-selector">
        <div className="topic-selector-container">
          <h2>טוען נושאים זמינים...</h2>
          <div className="loading-spinner"></div>
        </div>
      </div>
    );
  }

  if (availableTopics.length === 0) {
    return (
      <div className="topic-selector">
        <div className="topic-selector-container">
          <div className="header-with-back">
            <button onClick={handleBackToSubjects} className="back-to-grade-button">
              חזרה לבחירת נושא לימוד ↩
            </button>
            <h2>לא נמצאו נושאים זמינים לשכבה {gradeDisplayNames[selectedGrade] || 'שכבה'}</h2>
          </div>
          <p>בדקו שיש קובץ CSV מתאים בתיקיית public/ בשם המתאים לשכבה.</p>
        </div>
      </div>
    );
  }

  const isJuniorHighVocab = selectedGrade === 'grade7';

  const maxPathTopics = 20;
  const supportsPathLayout =
    selectedGrade === 'grade10' || selectedGrade === 'grade11' || selectedGrade === 'grade7';
  const shouldUsePathLayout = supportsPathLayout && availableTopics.length <= maxPathTopics;
  const displayTopics = shouldUsePathLayout ? availableTopics.slice(0, maxPathTopics) : availableTopics;
  const hasSecondPath = displayTopics.length > 7;
  const hasThirdPath = isJuniorHighVocab && displayTopics.length > 14;
  const mobilePathViewBox = hasSecondPath || isJuniorHighVocab ? '0 0 400 1700' : '0 0 400 2000';

  return (
    <div className="topic-selector">
      <div className="topic-selector-container">
        <div className="header-with-back">
          <button onClick={handleBackToSubjects} className="back-to-grade-button">
            חזרה לבחירת נושא לימוד ↩
          </button>
          <h2 style={{ paddingTop: '20px' }}>{gradeDisplayNames[selectedGrade] || 'שכבה'} - {subjectNames[subject] || 'אוצר מילים'}</h2>
        </div>

        <p className="topic-selector-subtitle">בחר את הנושא שברצונך לתרגל</p>

        {shouldUsePathLayout ? (
          <div className={`vocab-games-container ${isJuniorHighVocab ? 'junior-high' : ''} ${hasSecondPath ? 'has-second-path' : ''}`.trim()}>
            <div className="paths-wrapper">
              <div className="path-container">
                <svg className="path-svg path-desktop" viewBox="0 0 1400 900" preserveAspectRatio="none">
                  <path
                    d={DESKTOP_PATH}
                    fill="none"
                    stroke="rgba(255, 255, 255, 0.3)"
                    strokeWidth="6"
                    strokeDasharray="15,10"
                    strokeLinecap="round"
                  />
                </svg>
                <svg className="path-svg path-mobile" viewBox={mobilePathViewBox} preserveAspectRatio="none">
                  <path
                    d={MOBILE_PATH}
                    fill="none"
                    stroke="rgba(255, 255, 255, 0.3)"
                    strokeWidth="6"
                    strokeDasharray="15,10"
                    strokeLinecap="round"
                  />
                </svg>
              </div>
              {hasSecondPath && (
                <div className="path-container path-container-second">
                  <svg className="path-svg path-desktop" viewBox="0 0 1400 900" preserveAspectRatio="none">
                    <path
                      d={DESKTOP_PATH}
                      fill="none"
                      stroke="rgba(255, 255, 255, 0.3)"
                      strokeWidth="6"
                      strokeDasharray="15,10"
                      strokeLinecap="round"
                    />
                  </svg>
                  <svg className="path-svg path-mobile" viewBox={mobilePathViewBox} preserveAspectRatio="none">
                    <path
                      d={MOBILE_PATH}
                      fill="none"
                      stroke="rgba(255, 255, 255, 0.3)"
                      strokeWidth="6"
                      strokeDasharray="15,10"
                      strokeLinecap="round"
                    />
                  </svg>
                </div>
              )}
              {hasThirdPath && (
                <div className="path-container path-container-third junior-high-third-path">
                  <svg className="path-svg path-desktop" viewBox="0 0 1400 900" preserveAspectRatio="none">
                    <defs>
                      <clipPath id="junior-high-third-desktop-clip" clipPathUnits="userSpaceOnUse">
                        <polygon points="430,0 1400,0 1400,900 0,900 0,185 430,185" />
                      </clipPath>
                    </defs>
                    <path
                      d={DESKTOP_PATH}
                      fill="none"
                      stroke="rgba(255, 255, 255, 0.3)"
                      strokeWidth="6"
                      strokeDasharray="15,10"
                      strokeLinecap="round"
                      clipPath="url(#junior-high-third-desktop-clip)"
                    />
                  </svg>
                  <svg className="path-svg path-mobile" viewBox={mobilePathViewBox} preserveAspectRatio="none">
                    <path
                      d={MOBILE_PATH}
                      fill="none"
                      stroke="rgba(255, 255, 255, 0.3)"
                      strokeWidth="6"
                      strokeDasharray="15,10"
                      strokeLinecap="round"
                    />
                  </svg>
                </div>
              )}
            </div>
            {displayTopics.map((topic) => (
              <div
                key={topic.id}
                className="vocab-game-bubble"
                onClick={() => handleTopicSelect(topic)}
              >
                <div className="bubble-circle">
                  <div
                    className={`bubble-icon${topic.id === 'verbs-afala4and-tafaala5' ? ' afala-tafaala-icon' : ''}`}
                    style={{ color: topic.iconColor || 'inherit' }}
                  >
                    {topic.icon}
                  </div>
                </div>
                <div className="bubble-text">
                  <h3 className="bubble-name">{topic.name}</h3>
                  <p className="bubble-description">{topic.description}</p>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="topics-grid">
            {displayTopics.map((topic) => (
              <div
                key={topic.id}
                className="topic-card"
                onClick={() => handleTopicSelect(topic)}
              >
                <div className="topic-icon">{topic.icon}</div>
                <h3 className="topic-name">{topic.name}</h3>
                <p className="topic-description">{topic.description}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default TopicSelector;
